import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueries, useSuspenseQuery } from '@tanstack/react-query'
import { Layers } from 'lucide-react'

import { PageContainer } from '@/components/common/page-container'
import { PageHeader } from '@/components/common/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { Pagination } from '@/components/ui/pagination'
import {
  MatchFilterBar,
  type MatchFilter,
} from '@/features/matches/match-filter-bar'
import { MatchTable } from '@/features/matches/match-table'
import { MatchDrawer } from '@/features/matches/match-drawer'
import {
  APPLIED_STATUSES,
  matchesListQuery,
  type Match,
  type MatchListFilters,
} from '@/api/matches'
import { RouteError } from '@/components/common/route-error'
import { useDebounced } from '@/lib/use-debounced'
import { extensionQuestionsQuery } from '@/api/questions'

/**
 * /matches — cross-loop list with drawer (Q9 layout).
 *
 * Filtering is server-side (P1.6): each chip + the search box are
 * translated into a `MatchListFilters` object and pushed into the
 * `/api/matches` query string. Counts in chip badges come from cheap
 * count-only queries (perPage=1, read `.total`) and therefore reflect
 * the whole account, not just the current page.
 */

const DEFAULT_PER_PAGE = 100
const SEARCH_DEBOUNCE_MS = 300

/**
 * Chip → server filter mapping. Kept here (not in the bar) because the
 * bar stays presentational and reusable; the server contract lives with
 * the page that talks to the API.
 */
function filtersForChip(chip: MatchFilter): MatchListFilters | undefined {
  switch (chip) {
    case 'all':
      return undefined
    case 'applied':
      return { status: APPLIED_STATUSES }
    case 'needs_answers':
      return { status: ['FORM_HAS_QUESTIONS'] }
    case 'easy_apply':
      // easyApply=true covers extension matches regardless of upstream status.
      return { easyApply: true }
    case 'ready':
      return { status: ['ONLY_MATCH', 'ONLY_MATCH_WITH_FORM'] }
  }
}

const COUNT_CHIPS: MatchFilter[] = [
  'all',
  'ready',
  'applied',
  'needs_answers',
  'easy_apply',
]

// `?filter=` deep-link support so dashboard KPIs (e.g. "Needs answers")
// can land here pre-filtered. Anything unrecognised collapses to 'all'.
function parseFilterParam(raw: unknown): MatchFilter {
  return COUNT_CHIPS.includes(raw as MatchFilter) ? (raw as MatchFilter) : 'all'
}

export const Route = createFileRoute('/_authed/matches')({
  validateSearch: (search: Record<string, unknown>) => ({
    filter: parseFilterParam(search.filter),
  }),
  loader: async ({ context }) => {
    // Local pending questions ship with the page: they decide whether a row
    // gets the "Answer →" CTA even though LoopCV thinks nothing is waiting.
    await Promise.all([
      context.queryClient.ensureQueryData(matchesListQuery(1, DEFAULT_PER_PAGE)),
      context.queryClient.ensureQueryData(extensionQuestionsQuery()),
    ])
  },
  component: MatchesPage,
  errorComponent: RouteError,
})

function MatchesPage() {
  const { filter: filterParam } = Route.useSearch()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Match | null>(null)
  // Which drawer tab to open on: 'qa' when arriving via the "Answer →"
  // CTA, 'desc' for a plain row click.
  const [openTab, setOpenTab] = useState('desc')
  const [filter, setFilter] = useState<MatchFilter>(filterParam)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search.trim(), SEARCH_DEBOUNCE_MS)

  // Compose the active server filter from chip + search. Memoize so the
  // queryOptions identity is stable across renders that don't change input.
  const activeFilters = useMemo<MatchListFilters | undefined>(() => {
    const base = filtersForChip(filter) ?? {}
    const withSearch: MatchListFilters = debouncedSearch
      ? { ...base, keywords: [debouncedSearch] }
      : base
    // Collapse empty object back to undefined so the URL stays clean.
    return Object.keys(withSearch).length > 0 ? withSearch : undefined
  }, [filter, debouncedSearch])

  const { data } = useSuspenseQuery(
    matchesListQuery(page, DEFAULT_PER_PAGE, activeFilters),
  )
  // Questions the Apply Tool parked locally, keyed by match. Answered sets
  // are filtered out server-side, so this is exactly what is still waiting.
  const { data: localSets } = useSuspenseQuery(extensionQuestionsQuery())
  const localPending = useMemo(() => new Set(localSets.map((s) => s.matchId)), [localSets])

  // Per-chip account-wide counts. perPage=1 keeps each request to a
  // single row; the chip badges only consume `.total`. Search is folded
  // in so the badges stay honest while the user is typing.
  const countQueries = useQueries({
    queries: COUNT_CHIPS.map((chip) => {
      const base = filtersForChip(chip) ?? {}
      const f: MatchListFilters = debouncedSearch
        ? { ...base, keywords: [debouncedSearch] }
        : base
      return matchesListQuery(1, 1, Object.keys(f).length > 0 ? f : undefined)
    }),
  })
  const counts = useMemo(() => {
    const out: Partial<Record<MatchFilter, number>> = {}
    COUNT_CHIPS.forEach((chip, i) => {
      const total = countQueries[i]?.data?.total
      if (typeof total === 'number') out[chip] = total
    })
    return out
  }, [countQueries])

  return (
    <PageContainer>
      <PageHeader
        title="Matches"
        description="Every job our loops have surfaced. Filter by apply path, then click for details."
      />

      <MatchFilterBar
        filter={filter}
        onFilterChange={(f) => {
          setFilter(f)
          // Switching filter changes the result set — page 1 is the only sane reset.
          setPage(1)
          setSelected(null)
        }}
        search={search}
        onSearchChange={(s) => {
          setSearch(s)
          setPage(1)
        }}
        counts={counts}
      />

      {data.items.length === 0 ? (
        <EmptyState
          icon={Layers}
          title={filter === 'all' && !debouncedSearch ? 'No matches yet' : 'Nothing matches your filter'}
          description={
            filter === 'all' && !debouncedSearch
              ? 'Create a loop and we will start matching jobs within a minute.'
              : 'Try a different filter or clear the search.'
          }
        />
      ) : (
        <MatchTable
          items={data.items}
          selectedId={selected?.id}
          localPending={localPending}
          onSelect={(m) => {
            setOpenTab('desc')
            setSelected(m)
          }}
          onAnswer={(m) => {
            // Local questions are answered on /questions (the tool reads them
            // back on its next run); only LoopCV's own belong in the drawer.
            if (localPending.has(m.id)) {
              void navigate({ to: '/questions' })
              return
            }
            setOpenTab('qa')
            setSelected(m)
          }}
        />
      )}

      <Pagination
        page={page}
        perPage={DEFAULT_PER_PAGE}
        total={data.total}
        onPageChange={(p) => {
          setPage(p)
          setSelected(null)
        }}
        className="mt-4"
      />

      <MatchDrawer
        matchId={selected?.id ?? null}
        hint={selected}
        open={!!selected}
        initialTab={openTab}
        onClose={() => setSelected(null)}
      />
    </PageContainer>
  )
}
